import type { EquipmentProcessType } from '../equipment/equipmentTypes';
import { normalizeEquipmentProcessType } from '../equipment/equipmentConstants';

/**
 * Process flow — the ordered route a FOUP takes through the fab.
 *
 * Start (entry gate) and End (exit gate) are implicit and always bracket the
 * user-defined steps, so only the middle process stages are stored here. The
 * process flow lives in `layout.projectMetadata.processFlow` so it saves and loads with
 * the layout JSON alongside `inventory` / `transportFlow`.
 */

/** Process time prepopulated for a tool the first time it enters a process flow step. */
export const DEFAULT_PROCESS_FLOW_TIME_SEC = 10;

/** Default number of process-flow cycles (how many times the FOUP route repeats). */
export const DEFAULT_PROCESS_FLOW_CYCLE_COUNT = 50;

export const PROCESS_FLOW_VERSION = 1;

/**
 * Layout `processWaitSec` and process-flow `toolProcessTimes` are wall seconds
 * in the UI. The live websocket payload multiplies them by `simSpeed`.
 */
export type ProcessFlowStep = {
  id: string;
  processType: EquipmentProcessType;
  /**
   * Stockers dedicated to buffering FOUPs for this process.
   * `null` or `[]` means none.
   */
  stockerIds: string[] | null;
  /** Per-tool process time in wall seconds, keyed by equipment instance id. */
  toolProcessTimes: Record<string, number>;
  /** Per-tool active flag, keyed by equipment instance id. Missing keys default to active. */
  toolActive: Record<string, boolean>;
  /**
   * Intrabay visit order for tools in this process step. Drives `{ TOOL: … }` hops
   * in the bay OHT `dedicatedPath`.
   */
  toolOrderIds: string[];
};

export type ProcessFlow = {
  version: number;
  /** How many times the configured process route repeats. Default 50. */
  cycleCount: number;
  steps: ProcessFlowStep[];
};

export const EMPTY_PROCESS_FLOW: ProcessFlow = {
  version: PROCESS_FLOW_VERSION,
  cycleCount: DEFAULT_PROCESS_FLOW_CYCLE_COUNT,
  steps: [],
};

export function createProcessFlowStepId(): string {
  return `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function asProcessType(value: unknown): EquipmentProcessType {
  return normalizeEquipmentProcessType(value);
}

function asProcessTimes(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object') return {};
  const times: Record<string, number> = {};
  for (const [toolId, raw] of Object.entries(value as Record<string, unknown>)) {
    const seconds = Number(raw);
    if (Number.isFinite(seconds) && seconds >= 0) times[toolId] = seconds;
  }
  return times;
}

function asToolActive(value: unknown): Record<string, boolean> {
  if (!value || typeof value !== 'object') return {};
  const active: Record<string, boolean> = {};
  for (const [toolId, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw === 'boolean') active[toolId] = raw;
  }
  return active;
}

function asToolOrderIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value
        .map((id) => (id == null ? '' : String(id).trim()))
        .filter(Boolean)
    ),
  ];
}

/** Normalize stocker ids from `stockerIds` or legacy single `stockerId`. */
export function readStepStockerIds(step: Record<string, unknown>): string[] | null {
  if (step.stockerIds === null) return null;
  if (Array.isArray(step.stockerIds)) {
    const ids = step.stockerIds
      .map((id) => (id == null ? '' : String(id).trim()))
      .filter(Boolean);
    return ids.length === 0 ? null : ids;
  }
  if (step.stockerId == null || step.stockerId === '') return null;
  return [String(step.stockerId)];
}

/** Read the process flow out of layout project metadata, tolerating older/partial documents. */
export function readProcessFlow(
  projectMetadata: Record<string, unknown> | null | undefined
): ProcessFlow {
  const rawSource =
    projectMetadata && typeof projectMetadata.processFlow === 'object' && projectMetadata.processFlow
      ? projectMetadata.processFlow
      : projectMetadata && typeof projectMetadata.recipe === 'object' && projectMetadata.recipe
        ? projectMetadata.recipe
        : null;
  const raw =
    rawSource && typeof rawSource === 'object' ? (rawSource as Record<string, unknown>) : null;
  if (!raw || !Array.isArray(raw.steps)) return EMPTY_PROCESS_FLOW;

  const steps = raw.steps.flatMap((entry): ProcessFlowStep[] => {
    if (!entry || typeof entry !== 'object') return [];
    const step = entry as Record<string, unknown>;
    return [
      {
        id: String(step.id || createProcessFlowStepId()),
        processType: asProcessType(step.processType),
        stockerIds: readStepStockerIds(step),
        toolProcessTimes: asProcessTimes(step.toolProcessTimes),
        toolActive: asToolActive(step.toolActive),
        toolOrderIds: asToolOrderIds(step.toolOrderIds),
      },
    ];
  });

  const cycleRaw = Number(raw.cycleCount);
  const cycleCount =
    Number.isFinite(cycleRaw) && cycleRaw > 0
      ? Math.floor(cycleRaw)
      : DEFAULT_PROCESS_FLOW_CYCLE_COUNT;

  return { version: PROCESS_FLOW_VERSION, cycleCount, steps };
}

/**
 * AMHS payload fields derived from Process Flow Config.
 * Falls back to `fallbackProcessTypes` when no steps are saved yet.
 */
export function buildAmhsProcessSequence(
  projectMetadata: Record<string, unknown> | null | undefined,
  fallbackProcessTypes: readonly string[] = []
): { processSequence: string[]; numberOfCycles: number } {
  const flow = readProcessFlow(projectMetadata);
  const fromSteps = flow.steps.map((step) => step.processType);
  const processSequence =
    fromSteps.length > 0
      ? fromSteps
      : [...new Set(fallbackProcessTypes.map((type) => String(type).trim().toUpperCase()).filter(Boolean))];
  return {
    processSequence,
    numberOfCycles: flow.cycleCount,
  };
}

/**
 * Keep saved tool order, drop removed tools, append newly discovered tools.
 */
export function syncToolOrderIds(
  saved: readonly string[],
  available: readonly string[]
): string[] {
  const availableSet = new Set(available);
  const ordered = saved.filter((id) => availableSet.has(id));
  for (const id of available) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  return ordered;
}

/** Render tools in the saved process-step order (unknown tools append at end). */
export function toolsInStepOrder<T extends { id: string }>(
  step: ProcessFlowStep,
  tools: readonly T[]
): T[] {
  const byId = new Map(tools.map((tool) => [tool.id, tool]));
  const ordered: T[] = [];
  for (const id of step.toolOrderIds) {
    const tool = byId.get(id);
    if (tool) ordered.push(tool);
  }
  for (const tool of tools) {
    if (!step.toolOrderIds.includes(tool.id)) ordered.push(tool);
  }
  return ordered;
}

/**
 * Active tool ids in process-flow order for intrabay `dedicatedPath` TOOL hops.
 * Falls back to encounter order when no step is saved yet.
 */
export function resolveBayPathToolIds(
  step: { toolOrderIds?: string[]; toolActive?: Record<string, boolean> } | undefined,
  tools: readonly { toolId: string; toolType: string }[],
  processType: string
): string[] {
  const normalizedType = String(processType).trim().toUpperCase();
  const matching = tools.filter(
    (tool) => String(tool.toolType).trim().toUpperCase() === normalizedType
  );
  const matchingIds = new Set(matching.map((tool) => tool.toolId));
  const ordered: string[] = [];
  for (const id of step?.toolOrderIds ?? []) {
    if (matchingIds.has(id)) ordered.push(id);
  }
  for (const tool of matching) {
    if (!ordered.includes(tool.toolId)) ordered.push(tool.toolId);
  }
  return ordered.filter((id) => step?.toolActive?.[id] !== false);
}

/**
 * Fill in defaults for tools that just became part of a step, and drop entries
 * for tools that no longer belong to its process type. New tools default to
 * active with `DEFAULT_PROCESS_FLOW_TIME_SEC`.
 */
export function syncStepProcessTimes(
  step: ProcessFlowStep,
  toolIds: readonly string[],
  defaultProcessTimes: Readonly<Record<string, number>> = {}
): ProcessFlowStep {
  const toolProcessTimes: Record<string, number> = {};
  const toolActive: Record<string, boolean> = {};
  for (const toolId of toolIds) {
    toolProcessTimes[toolId] =
      step.toolProcessTimes[toolId] ??
      defaultProcessTimes[toolId] ??
      DEFAULT_PROCESS_FLOW_TIME_SEC;
    toolActive[toolId] = step.toolActive?.[toolId] ?? true;
  }
  return {
    ...step,
    toolProcessTimes,
    toolActive,
    toolOrderIds: syncToolOrderIds(step.toolOrderIds ?? [], toolIds),
  };
}
